
import { ShipmentId } from "@/domain/entities/Booking";
import OverlayPanel from "primevue/overlaypanel";
import { Options, prop, Vue } from "vue-class-component";
import EmptyList from "@/app/ui/components/status-handler/index.vue";
import mapProductTypeName from "@/app/infrastructures/misc/common-library/ProductTypeNameMapper";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { isCustomerApps } from "@/app/ui/views/track-delivery/delivery-tab/modules";
import { ShipmentIdTable } from "@/domain/entities/CreateBookingV3";
import { FcsPermissionPermissionBookingV3 } from "@/feature-flags/flag-booking-v3";
import formatDate from "@/app/infrastructures/misc/common-library/v2/FormatDate";
import { formatByCountry } from "@/app/infrastructures/misc/Utils";
import Icons from "@/app/ui/components/icons/index.vue";
import { FlagsPermissionBookingV4 } from "@/feature-flags/flag-booking-v4";


export interface ShipmentDataProps {
  shipmentId: string;
  data: ShipmentIdTable[];
}

class Props {
  shipmentData = prop<ShipmentDataProps[]>({
    default: () => [],
    required: true
  });
}

@Options({
  components: {
    OverlayPanel,
    EmptyList,
    Icons
  }
})
class ShipmentTable extends Vue.with(Props) {
  sttToBePrinted = 0;
  sttNo = "";
  printRowId = 0;
  mapProductTypeName = mapProductTypeName;
  formatDate = formatDate;

  // getters
  get shipmentIdUniqueIds() {
    const ids: number[] = [];
    this.shipmentData.forEach((group) => {
      group.data.forEach((shipmentId) => {
        ids.push(shipmentId.id);
      });
    });

    return ids;
  }

  get totalAllItems() {
    return this.shipmentData.flatMap((s) => s.data);
  }

  get isShowExistBooked() {
    return FcsPermissionPermissionBookingV3.exist_booked.isEnabled();
  }

  // action methods
  togglePrintPanel(event: any, sttId = 0, sttNo = "", rowId = 0) {
    this.sttToBePrinted = sttId;
    this.sttNo = sttNo;
    this.printRowId = rowId;
    (this.$refs["shipment-booking-print"] as any).toggle(event);
  }

  // emits
  bookingShipment(shipmentId = "", id = 0, version = "") {
    this.$emit("bookingShipment", shipmentId, id, version);
  }

  editShipment(shipmentId = "", id = 0, version = "") {
    this.$emit("editShipment", shipmentId, id, version);
  }

  deleteShipment(shipmentId = "", id = 0) {
    this.$emit("deleteShipment", shipmentId, id);
  }

  addAddress(shipmentId = "") {
    this.$emit("addAddress", shipmentId);
  }

  print(printType = "print-basic") {
    this.$emit("print", this.sttToBePrinted, this.sttNo, printType);
  }

  isCA(shipmentId = ""): boolean {
    return isCustomerApps(shipmentId);
  }

  isC1OrC2(shipmentId = ""): boolean {
    return /^(C1|C2)/.test(shipmentId);
  }

  get formatHidePrice() {
    return !this.isAccountForeign ? "Rp••••••" : "RM••••••";
  }
  get isAccountForeign() {
    return AccountController.accountData.accountIsForeign;
  }

  get accountCurrency(): "IDR" | "MYR" {
    return (AccountController.accountData?.accountCurrency ?? "IDR") as
      | "IDR"
      | "MYR";
  }

  isRenderHeadGroup(shipmentId = "") {
    return /^(AS|AP|G)/.test(shipmentId);
  }
  isApAs(shipmentId = "") {
    return /^(AS|AP)/.test(shipmentId);
  }

  mapBookingStatus(sttNo = "", error: string | null = null) {
    if (sttNo) {
      return {
        class: "bg-green-lp-300 text-green-lp-400",
        text: this.$t("bookingShipment.appsMarketplace.success")
      };
    }
    if (error) {
      return {
        class: "bg-red-lp-600 text-red-lp-100",
        text: this.$t("bookingShipment.appsMarketplace.failedWithReason", {
          reason: error
        })
      };
    }

    return {
      class: "bg-gray-lp-200 text-gray-lp-300",
      text: this.$t("bookingShipment.appsMarketplace.unbooked")
    };
  }

  renderDeliveryType(shipment: ShipmentId) {
    const isCOD = [shipment.shipmentIsCod, shipment.STT?.sttIsCod].includes(
      true
    );
    const isDFOD = [shipment.shipmentIsDfod, shipment.STT?.sttIsDfod].includes(
      true
    );
    if (!isCOD) return false;
    if (isCOD && !isDFOD) return require("@/app/ui/assets/svg/cod-logo-2.svg");
    if (isCOD && isDFOD) return require("@/app/ui/assets/svg/new-dfod.svg");
    return false;
  }

  indexOffset(index = 0) {
    let count = 0;
    for (let k = 0; k < index; k++) {
      count += this.shipmentData[k].data.length;
    }
    return count;
  }

  get accountCountryCode() {
    return AccountController.accountData.countryCode?.toUpperCase();
  }

  formatWeight(value: number) {
    return formatByCountry(value, {
      type: "weight",
      country: this.accountCountryCode,
      suffix: " kg"
    });
  }

  formatQuantity(value: number) {
    return formatByCountry(value, {
      type: "quantity",
      country: this.accountCountryCode
    });
  }

  formatPrice(value: number) {
    return formatByCountry(value, {
      type: "money",
      country: this.accountCountryCode,
      prefix: this.accountCountryCode?.toUpperCase() === "MY" ? "RM" : "Rp"
    });
  }

  get isBookingMarketplaceV3() {
    return FlagsPermissionBookingV4.permission_booking_v3_create_markeplace.isEnabled();
  }

  get isBookingMarketplaceV4() {
    return FlagsPermissionBookingV4.permission_booking_v4_create_markeplace.isEnabled();
  }

  get bookingVersion() {
    return this.isBookingMarketplaceV4 ? "v4" : "v3";
  }
}

export default ShipmentTable;
